import React, { useRef, useState } from "react";
import moment from "moment";
import ReCAPTCHA from "../Recaptcha";
import { useApi } from "../../utils/api";
import { useAuth } from "../../utils/auth";
import {
  Grid2 as Grid,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Box,
  Container,
  Stack,
  Paper,
} from "@mui/material";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import SyncAltOutlinedIcon from "@mui/icons-material/SyncAltOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import VolunteerActivismOutlinedIcon from "@mui/icons-material/VolunteerActivismOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import heroCoachSignup from "./assets/coach-signup-hero-phone-gym.png";
import {
  marketingContainerSx,
  marketingPageRootSx,
} from "../../utils/marketingPageLayout";

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

const cardSx = {
  bgcolor: "#fff",
  borderRadius: 2,
  borderColor: "rgba(0,0,0,0.08)",
  boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 4px 14px rgba(0,0,0,0.06)",
};

const highlights = [
  {
    Icon: AssignmentTurnedInOutlinedIcon,
    title: "Programming that scales",
    body: "Lay out workouts, progressions, and coaching notes clients actually follow—all in one place.",
  },
  {
    Icon: GroupsOutlinedIcon,
    title: "Clients stay engaged",
    body: "Logging, messaging, and history stay organized so athletes know what’s next without the back-and-forth.",
  },
  {
    Icon: PaymentsOutlinedIcon,
    title: "Business in one lane",
    body: "Subscriptions and payments plug in when you’re ready, so paperwork doesn’t swallow coaching time.",
  },
  {
    Icon: VolunteerActivismOutlinedIcon,
    title: "White-glove onboarding",
    body: "We walk you through setup, migrating clients, and importing programs—so you're productive from week one.",
  },
  {
    Icon: SyncAltOutlinedIcon,
    title: "Get programs in with ease",
    body: "Bring over what you’ve already built. We focus on smoothing the transition, not restarting from scratch.",
  },
];

const CoachSignUpLanding = ({
  headline = "Coach with Trainer App",
  subheadline = "Start your one-month free trial. No contracts—cancel anytime.",
  promoCode,
  promoEventName,
}) => {
  const { createUser } = useApi();
  const { login } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const recaptchaRef = useRef(null);

  const resetCaptcha = () => {
    setCaptchaToken("");
    recaptchaRef.current?.reset();
  };

  const handleCreateCoachAccount = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      setIsLoading(false);
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      setIsLoading(false);
      return;
    }

    if (RECAPTCHA_SITE_KEY && !captchaToken) {
      setError("Please verify that you are not a robot.");
      setIsLoading(false);
      return;
    }

    try {
      const response = await createUser({
        name,
        email,
        password,
        isCoach: true,
        createdDate: moment().unix(),
        ...(captchaToken && { captchaToken }),
        ...(promoCode && { signupPromo: promoCode }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (typeof window.gtag === "function") {
        window.gtag("event", "coach_sign_up");
      }

      await login(email, password);
      window.location.href = "/clients";
    } catch (err) {
      console.error("Error creating coach account:", err);
      setError("Failed to create account. Please try again.");
      resetCaptcha();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        ...marketingPageRootSx,
        bgcolor: "#fff",
        color: "#111",
        backgroundImage:
          "radial-gradient(ellipse 120% 80% at 100% 0%, rgba(255, 0, 0, 0.05), transparent 50%), radial-gradient(ellipse 80% 60% at 0% 100%, rgba(0, 0, 0, 0.03), transparent 45%)",
      }}
    >
      {/* Full-width hero banner */}
      <Box
        component="section"
        aria-label="Signup hero"
        sx={{
          position: "relative",
          width: "100%",
          overflow: "hidden",
          bgcolor: "#0d0d0d",
        }}
      >
        <Box
          component="img"
          src={heroCoachSignup}
          alt="Trainer App on a phone in the gym displaying workout tracking for upper day lifts"
          sx={{
            display: "block",
            width: "100%",
            height: { xs: 260, sm: 340, md: 400 },
            objectFit: "cover",
            objectPosition: "50% 40%",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.06) 0%, transparent 38%, transparent 74%, rgba(0,0,0,0.2) 100%)",
            pointerEvents: "none",
          }}
        />
      </Box>

      <Container
        maxWidth="xl"
        sx={{
          ...marketingContainerSx,
          pt: 0,
          pb: { xs: 5, md: 7 },
        }}
      >
        <Box
          sx={{
            mt: { xs: 3, sm: 3.5, md: 4 },
            mb: { xs: 2, md: 3 },
            position: "relative",
            zIndex: 2,
          }}
        >
          <Grid container spacing={{ xs: 2.5, md: 3 }} alignItems="flex-start">
            <Grid
              size={{ xs: 12, md: 7 }}
              sx={{ order: { xs: 2, md: 1 }, position: "relative", zIndex: 2 }}
            >
              <Paper
                variant="outlined"
                sx={{
                  ...cardSx,
                  overflow: "hidden",
                  p: { xs: 2.5, sm: 3, md: 3.5 },
                  boxShadow:
                    "0 8px 32px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.04)",
                }}
              >
                {promoEventName && (
                  <Box
                    sx={{
                      mb: 2.5,
                      position: "relative",
                      borderRadius: 2,
                      py: 2,
                      px: 2.25,
                      textAlign: "center",
                      overflow: "hidden",
                      background:
                        "linear-gradient(180deg, #ff4a4a 0%, #e21818 38%, #b91010 72%, #8f1616 100%)",
                      boxShadow:
                        "0 8px 28px rgba(200, 18, 18, 0.35), inset 0 1px 0 rgba(255,255,255,0.2)",
                      border: "1px solid rgba(255,255,255,0.22)",
                    }}
                  >
                    <Typography
                      component="p"
                      sx={{
                        m: 0,
                        color: "#fff",
                        fontWeight: 800,
                        fontSize: { xs: "0.98rem", sm: "1.08rem" },
                        lineHeight: 1.45,
                        textShadow: "0 1px 4px rgba(0,0,0,0.45)",
                      }}
                    >
                      {promoEventName}
                    </Typography>
                  </Box>
                )}
                <Typography
                  component="p"
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: "error.main",
                    mb: 1.5,
                  }}
                >
                  For coaches who are athletes.
                </Typography>
                <Typography
                  component="h1"
                  sx={{
                    fontSize: { xs: "2rem", sm: "2.35rem", md: "2.65rem" },
                    fontWeight: 900,
                    lineHeight: 1.05,
                    letterSpacing: -0.5,
                    mb: 2,
                  }}
                >
                  {headline}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "1.05rem", md: "1.125rem" },
                    lineHeight: 1.6,
                    color: "text.secondary",
                    mb: 2,
                  }}
                >
                  {subheadline}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1rem",
                    lineHeight: 1.65,
                    color: "rgba(0,0,0,0.78)",
                    mb: 0,
                  }}
                >
                  Trainer App is purpose-built personal trainer software: a
                  complete personal trainer platform for planning, tracking,
                  client comms, and optional billing—paired with a personal
                  training client app, and without noisy social features getting
                  in your way.
                </Typography>
              </Paper>
            </Grid>

            <Grid
              size={{ xs: 12, md: 5 }}
              sx={{
                order: { xs: 1, md: 2 },
                position: "relative",
                zIndex: 3,
              }}
            >
              <Paper
                variant="outlined"
                sx={{
                  ...cardSx,
                  p: { xs: 2.5, sm: 3 },
                  boxShadow:
                    "0 8px 32px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.04)",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 800, mb: 2, textAlign: "center" }}
                >
                  Create your coach account
                </Typography>
                <form onSubmit={handleCreateCoachAccount}>
                  <TextField
                    sx={{ mb: 2 }}
                    fullWidth
                    label="Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                  <TextField
                    sx={{ mb: 2 }}
                    fullWidth
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <TextField
                    sx={{ mb: 2 }}
                    fullWidth
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <TextField
                    sx={{ mb: 2 }}
                    fullWidth
                    label="Confirm Password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                  {RECAPTCHA_SITE_KEY && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        mb: 2,
                      }}
                    >
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={RECAPTCHA_SITE_KEY}
                        onChange={(token) => setCaptchaToken(token || "")}
                        onExpired={() => setCaptchaToken("")}
                        onErrored={() => setCaptchaToken("")}
                      />
                    </Box>
                  )}
                  {error && (
                    <Typography color="error" sx={{ mb: 2 }}>
                      {error}
                    </Typography>
                  )}
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={
                      isLoading || (Boolean(RECAPTCHA_SITE_KEY) && !captchaToken)
                    }
                    size="large"
                    sx={{
                      mt: 1,
                      py: 1.25,
                      fontWeight: 800,
                      textTransform: "none",
                      fontSize: "1rem",
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Create coach account & start trial"
                    )}
                  </Button>
                </form>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Stack
          spacing={2.25}
          sx={{ position: "relative", zIndex: 1, width: "100%" }}
        >
          {highlights.map(({ Icon, title, body }) => (
            <Paper
              key={title}
              variant="outlined"
              sx={{
                p: 2,
                display: "flex",
                gap: 2,
                alignItems: "flex-start",
                ...cardSx,
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  width: { xs: 56, sm: 60 },
                  height: { xs: 56, sm: 60 },
                  borderRadius: 1,
                  bgcolor: "rgba(255,0,0,0.08)",
                  color: "error.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon sx={{ fontSize: { xs: 30, sm: 32 } }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
                  {title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.925rem",
                    lineHeight: 1.55,
                    color: "text.secondary",
                  }}
                >
                  {body}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Stack>
      </Container>
    </Box>
  );
};

export default CoachSignUpLanding;
